import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const Dashboard: React.FC = () => {
  const features = [
    {
      title: 'CloudFlare Workers',
      description: 'Serverless functions running globally at the edge',
      icon: '⚡',
      status: 'Ready',
      link: '/api'
    },
    {
      title: 'D1 Database',
      description: 'Serverless SQLite database with automatic scaling',
      icon: '🗃️',
      status: 'Connected',
      link: '/users'
    },
    {
      title: 'R2 Storage',
      description: 'Object storage for files, images, and assets',
      icon: '📦',
      status: 'Ready',
      link: '/files'
    },
    {
      title: 'GoalYet AI',
      description: 'AI-powered project management and step tracking',
      icon: '🤖',
      status: 'Active',
      link: '/'
    }
  ];

  const stats = [
    { label: 'Total Steps Tracked', value: '99', icon: '🎯' },
    { label: 'Active Projects', value: '5', icon: '🚀' },
    { label: 'Files Uploaded', value: '42', icon: '📁' },
    { label: 'API Calls Today', value: '1,337', icon: '🔌' }
  ];

  return (
    <div className="space-y-8">

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-orange-100 p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">{stat.icon}</span>
              <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
            </div>
            <p className="text-sm text-gray-600">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-orange-100 p-6 hover:shadow-lg transition-shadow"
          >
            <Link to={feature.link} className="block">
              <div className="flex items-start justify-between mb-4">
                <div className="text-3xl">{feature.icon}</div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  feature.status === 'Connected' || feature.status === 'Active' || feature.status === 'Ready'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {feature.status}
                </span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Quick Start */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4">🚀 Quick Start Guide</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
            <span>Chat with the <strong>AI Bot</strong> to plan your next feature</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
            <span>Track implementation steps in <strong>GoalYet</strong></span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
            <span>Manage data with <strong>Users</strong> CRUD operations</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
            <span>Upload assets to <strong>R2 Storage</strong> via Files</span>
          </div>
        </div>
      </div>
    </div>
  );
};